import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('nrt_token'));
  const [loading, setLoading] = useState(true);

  const applyToken = useCallback((t) => {
    if (t) {
      localStorage.setItem('nrt_token', t);
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    } else {
      localStorage.removeItem('nrt_token');
      delete axios.defaults.headers.common['Authorization'];
    }
    setToken(t);
  }, []);

  const applyProfile = useCallback((p) => {
    if (p) {
      localStorage.setItem('nrt_profile_id', p.id);
      axios.defaults.headers.common['X-Profile-Id'] = p.id;
    } else {
      localStorage.removeItem('nrt_profile_id');
      delete axios.defaults.headers.common['X-Profile-Id'];
    }
    setCurrentProfile(p);
  }, []);

  const fetchProfiles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profiles`);
      setProfiles(data.profiles || []);
      return data.profiles || [];
    } catch (e) {
      console.error('[Auth] fetchProfiles failed:', e);
      setProfiles([]);
      return [];
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('nrt_token');
    if (!stored) {
      setLoading(false);
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
        const ps = await fetchProfiles();
        const storedId = localStorage.getItem('nrt_profile_id');
        if (storedId) {
          const p = ps.find((x) => x.id === storedId);
          if (p) applyProfile(p);
        }
      } catch (e) {
        console.error('[Auth] restore session failed:', e);
        applyToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [applyToken, applyProfile, fetchProfiles]);

  const sendOtp = useCallback(async (phone) => (await axios.post(`${API}/auth/send-otp`, { phone })).data, []);

  const verifyOtp = useCallback(async (phone, code) => {
    const { data } = await axios.post(`${API}/auth/verify-otp`, { phone, code });
    applyToken(data.token);
    setUser({ ...data.user, is_admin: data.user?.is_admin || false });
    applyProfile(null);
    await fetchProfiles();
    return data;
  }, [applyToken, applyProfile, fetchProfiles]);

  const logout = useCallback(() => {
    applyToken(null);
    applyProfile(null);
    setUser(null);
    setProfiles([]);
  }, [applyToken, applyProfile]);

  const selectProfile = useCallback((p) => applyProfile(p), [applyProfile]);
  const switchProfile = useCallback(() => applyProfile(null), [applyProfile]);

  const createProfile = useCallback(async (payload) => {
    const { data } = await axios.post(`${API}/profiles`, payload);
    setProfiles((prev) => [...prev, data]);
    return data;
  }, []);

  const updateProfile = useCallback(async (id, payload) => {
    const { data } = await axios.patch(`${API}/profiles/${id}`, payload);
    setProfiles((prev) => prev.map((p) => (p.id === id ? data : p)));
    setCurrentProfile((prev) => (prev?.id === id ? data : prev));
    return data;
  }, []);

  const deleteProfile = useCallback(async (id) => {
    await axios.delete(`${API}/profiles/${id}`);
    setProfiles((prev) => prev.filter((p) => p.id !== id));
    setCurrentProfile((prev) => {
      if (prev?.id === id) {
        applyProfile(null);
        return null;
      }
      return prev;
    });
  }, [applyProfile]);

  const isKidRestricted =
    !!currentProfile?.is_kid && currentProfile?.kid_age != null && currentProfile.kid_age < 14;

  const value = useMemo(() => ({
    user, token, loading,
    isAdmin: !!user?.is_admin,
    profiles, currentProfile, isKidRestricted,
    sendOtp, verifyOtp, logout,
    refreshProfiles: fetchProfiles,
    selectProfile, switchProfile,
    createProfile, updateProfile, deleteProfile,
  }), [
    user, token, loading, profiles, currentProfile, isKidRestricted,
    sendOtp, verifyOtp, logout, fetchProfiles,
    selectProfile, switchProfile, createProfile, updateProfile, deleteProfile,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
